import { apiService } from '@shared/services/apiServices';
import type {
  ListMyAssignedContactRequestsParams,
  ListContactRequestsResponse,
} from '../types/contactRequestTypes';
import contactRequestEndpoints from './contactRequestEndpoints';

/**
 * Fetches the contact requests assigned to the logged-in admin
 * (`GET /contact-requests/my-tasks`). The endpoint is ADMIN-only and
 * self-scoped — a 403 is surfaced (via a thrown error carrying `status`) so the
 * caller can render the access-denied screen; any other error resolves to an
 * empty page so the list degrades gracefully.
 */
export const listMyAssignedContactRequestsApiService = {
  listMyAssignedContactRequests: async (
    params?: ListMyAssignedContactRequestsParams
  ): Promise<ListContactRequestsResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      queryParams.append('endDate', params.endDate);
    }
    if (params?.sortOrder) {
      queryParams.append('sortOrder', params.sortOrder);
    }

    const qs = queryParams.toString();
    const endpoint = `${contactRequestEndpoints.MY_ASSIGNED_CONTACT_REQUESTS}${qs ? `?${qs}` : ''}`;
    const response = await apiService.getAPI(endpoint, {
      skipGlobalErrorRedirect: true,
    });

    if (response.error) {
      if (response.status === 403) {
        const err = new Error('Forbidden') as Error & { status: number };
        err.status = 403;
        throw err;
      }
      return {
        data: [],
        pagination: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 10,
          totalCount: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    return response.data as ListContactRequestsResponse;
  },
};
